<script lang="ts">
export default {
  name: 'FavoriteIndicator',
};
</script>

<script setup lang="ts">
import { computed } from 'vue';
import { Star } from 'lucide-vue-next';

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
}>();

const props = withDefaults(defineProps<{
  modelValue?: boolean,
  disabled?: boolean,
  loading?: boolean,
  onlyOn?: boolean,
  small?: boolean,
  clickable?: boolean,
  tooltipPosition?: 'top' | 'right' | 'bottom' | 'left' | undefined,
  size?: 'sm' | 'md' | 'lg' | undefined,
}>(), {
  modelValue: false,
  disabled: false,
  loading: false,
  onlyOn: false,
  small: false,
  clickable: true,
  tooltipPosition: 'bottom',
  size: 'md',
});

const classList = computed(() => {
    const classList = {
      disabled: props.disabled,
      small: props.small,
      clickable: props.clickable,
    } as Record<string, boolean>;

    if (props.tooltipPosition) {
      classList[tooltipPosition.value] = props.clickable;
    }

    return classList;
  },
);

const tooltipPosition = computed(() => {
  if (props.tooltipPosition) {
    return `tw:tooltip tw:tooltip-${props.tooltipPosition}`;
  }
  return '';
});

const iconSize = computed(() => {
  switch (props.size) {
    case 'sm':
      return 13;
    case 'md':
      return 16;
    case 'lg':
      return 19;
    default:
      return 16;
  }
});

function clicked() {
  emit('update:modelValue', !props.modelValue);
}

</script>

<template>
  <v-progress-circular v-if="loading" :size="16" color="primary" indeterminate></v-progress-circular>

  <div v-else
       class="tw:flex root tw:hover:cursor-pointer" :class="classList" @click="clicked"
       data-test="favorite-icon" :data-tip="props.clickable ? 'Toggle Favorite' : undefined">
    <div v-if="props.modelValue" class="on tw:text-primary tw:text-md tw:flex tw:items-center">
      <Star :size="iconSize" class="tw:fill-gold tw:stroke-primary tw:stroke-1 " />
    </div>
    <div v-else class="off tw:text-lg tw:flex tw:items-center" :class="{ hide: onlyOn }">
      <Star :size="iconSize" class="tw:stroke-primary" />
    </div>
  </div>
</template>

<style scoped>
/*noinspection CssUnusedSymbol*/
.root.disabled {
  cursor: default;
  color: var(--v-gray-base);
  opacity: 0.5;
}

.root > div {
  font-size: 1.1rem;
}

.root.small > div {
  font-size: 0.8rem;
}

.root.clickable > div:hover {
  transform: scale(1.1);
}

.off {
  visibility: visible;
}

.off.hide {
  visibility: hidden;
}
</style>
